import { doc, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getBytes, deleteObject } from 'firebase/storage'
import { db, storage } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Admin-managed library: Firestore metadata (inScope) plus Storage
// refDocuments/** (any-admin write, any valid-role read). Storage path is
// refDocuments/smoke_* so leftover sweep can prefix-delete without touching
// real library files.

export default {
  id: 'refDocuments',
  label: 'Reference documents',
  description:
    'refDocuments metadata create/delete is admin + inScope; Storage library write is any-admin.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'reference document write',
        'Select a school before probing school-scoped refDocuments'
      )
      return
    }

    const docId = `smoke_ref_${ctx.runId.slice(-8)}`
    const metaRef = doc(db, 'refDocuments', docId)
    const fileRef = storageRef(storage, `refDocuments/smoke_${ctx.runId.slice(-8)}.txt`)
    const payload = new Blob([`smoke ref ${ctx.runId}`], { type: 'text/plain' })
    const expectWrite = RULE_EXPECTATIONS.refDocumentWrite(role)
    const expectStorageWrite = RULE_EXPECTATIONS.refDocumentStorageWrite(role)
    const expectStorageRead = RULE_EXPECTATIONS.refDocumentStorageRead()

    try {
      let created = false
      await step(
        `write refDocuments metadata (expected: ${fmtExpected(expectWrite)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(metaRef, {
              source: 'link',
              title: 'SMOKE ref',
              type: 'other',
              externalUrl: 'https://example.invalid/smoke',
              __smokeTest: true,
              __smokeRunId: ctx.runId,
              createdAt: serverTimestamp(),
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
          )
          created = allowed
          return allowed
            ? `${accessDetail(expectWrite, allowed)} — refDocuments/${docId}`
            : accessDetail(expectWrite, allowed)
        }
      )

      if (created) {
        await step('read refDocuments metadata (expected: allowed)', async () => {
          const { allowed } = await attempt(async () => {
            const snap = await getDoc(metaRef)
            if (!snap.exists()) throw new Error('Created refDocuments doc not found on read-back')
          })
          return accessDetail(true, allowed)
        })
        await step(
          `delete refDocuments metadata (expected: ${fmtExpected(expectWrite)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(metaRef))
            if (allowed) created = false
            return accessDetail(expectWrite, allowed)
          }
        )
      } else {
        step.skip('read/delete refDocuments metadata', 'Write denied — later steps skipped')
      }

      await step(
        `upload refDocuments storage file (expected: ${fmtExpected(expectStorageWrite)})`,
        async () => {
          const { allowed } = await attempt(() => uploadBytes(fileRef, payload))
          return allowed
            ? `${accessDetail(expectStorageWrite, allowed)} — refDocuments/smoke_*`
            : accessDetail(expectStorageWrite, allowed)
        }
      )

      if (expectStorageWrite) {
        await step(
          `download refDocuments storage file (expected: ${fmtExpected(expectStorageRead)})`,
          async () => {
            const { allowed } = await attempt(async () => {
              const bytes = await getBytes(fileRef)
              if (!bytes.byteLength) throw new Error('Downloaded file is empty')
            })
            return accessDetail(expectStorageRead, allowed)
          }
        )
      } else {
        step.skip('download refDocuments storage file', 'Upload denied — nothing to read')
      }
    } finally {
      await deleteDoc(metaRef).catch(() => {})
      await deleteObject(fileRef).catch(() => {})
    }
  }
}
